import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, ViewChild, Inject,
  Input, Output, EventEmitter, Renderer2, ElementRef, OnInit, OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable ,  Subject } from 'rxjs';
import * as _ from 'lodash';



import { ModalDirective } from 'ngx-bootstrap/modal';

import { DigitalAssetsService} from '../../../digital-assets/digital-assets.service';
import { BrandService} from '../../../brand/brand.service';
import { AuthService} from '../../../auth/auth.service';

import { BaseModalComponent } from '../../../modal/_base-modal.component';
import { LoaderComponent } from '../../../loader/loader.component';
import { DragAssetsSpaceComponent } from '../drag-assets-space/drag-assets-space.component';
import { SimpleConfirmModalComponent } from '../../../modal/simple-confirm-modal/simple-confirm-modal.component';

import { getUploaderAssetThumbnail, getAssetCategory, isValidAssetType, DigitalAsset, DigitalAssetRow,
  createDigitalAsset, invalidNameSymbols,
} from '../../../digital-assets/digital-asset-helpers';
import { mimetypesAsString } from '../../../digital-assets/mimetypes';
import { SortModel } from '../../../search/sort.model';
import { Config } from '../../../config/env.config';
import { UploadOutput, UploadInput, UploadFile, UploadStatus } from '../../../ngx-uploader/classes/ngx-uploader.class';
import { columns } from './assets-upload-columns';
import { duplicateAssetsColumns } from './duplicate-assets-columns';
import {delay, first, takeWhile} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import { editableAssetsColumns } from './editable-assets-upload-columns';

/*
  * Modal doesn't inherit default.modal.component because of necessity to be drag'n'dropable
*/
@Component({
  moduleId: module.id,
  selector: 'upload-assets-modal',
  templateUrl: 'upload-assets-modal.component.html',
  styleUrls: ['upload-assets-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadAssetsModalComponent implements OnInit, OnDestroy {

  @ViewChild('defaultModal', {static: false})
  defaultModal: ModalDirective | void;

  @ViewChild(LoaderComponent, {static: false})
  loader: LoaderComponent | void;

  @ViewChild(DragAssetsSpaceComponent, {static: false})
  dragAssetsSpace: DragAssetsSpaceComponent | void;

  @ViewChild('warningModal', {static: false})
  warningModal: SimpleConfirmModalComponent | void;

  @Input() isEditable: boolean = false;

  @Output() uploadModalClosed: EventEmitter<any> = new EventEmitter();
  @Output() successModalShow: EventEmitter<any> = new EventEmitter();
  @Output() assetsUploaded: EventEmitter<any> = new EventEmitter(); // response on upload asset API request (asset object)
  @Output() assetsSaved: EventEmitter<any> = new EventEmitter();

  assets: Array<DigitalAssetRow> = []; // for displaying assets in the modal
  invalidAssets: Array<DigitalAssetRow> = []; // keeps assets with invalid name
  duplicatedAssets: Array<DigitalAssetRow> = []; // keeps duplicates
  columns: Array<any> = [];
  uploadedAssets: any[] = []; // array which is used to emit all assets together when uploading is finished
  processedAssets: Array<DigitalAssetRow> = []; // for processed duplicated assets in editable mode
  processedUploadedAssets: any[] = []; // for api response in editable mode

  mimetypes = mimetypesAsString; // allowed for uploading mimetypes
  invalidNameSymbols: Array<string> = invalidNameSymbols;
  isInstantUploadingDone: boolean = false; // indicate first instant uploading
  displayingDuplicates: boolean = false; // indicate that duplicates are being displayed

  sortModel: any = null;
  selectedAllChecked: boolean = false;

  isUploadInProgress: boolean; // indicate uploading process
  uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>(); // ngx-uploader event emitter
  isModalShown: boolean = false;
  dragOver: boolean = false; // highlight dropable HTMl element

  private files: Array<any> = []; // all uploaded files
  private selectedFiles: Array<any> = []; // selected files (used while uploading)
  private isActive: boolean = true;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _renderer: Renderer2,
    private brandService: BrandService,
    private authService: AuthService,
    private damService: DigitalAssetsService,
    private ref: ChangeDetectorRef,
  ) {}

  /*******************************************
    * COMPONENT GETTERS
  *******************************************/

  get brandId(): any {
    return this.brandService.getBrand();
  }

  get isUploadStartedAndNotFinished(): boolean {
    return this.isUploadInProgress && this.uploadProgressPercent < 100;
  }

  get isUploadAlmostFinished(): boolean {
    return this.uploadProgressPercent >= 80 && this.uploadProgressPercent !== 100;
  }

  get isUploadFinished(): boolean {
    return this.uploadProgressPercent === 100;
  }

  get selectedAssets(): any {
    return _.filter(this.assets, {selected: true});
  }

  get headerText(): string {
    if (this.isInstantUploadingDone) {
      return 'Files uploaded';
    }
    return 'Assets to upload';
  }

  get uploadProgressPercent(): number {
    if (!this.selectedFiles.length) { return 0; }

    const totalPercent: number = this.selectedFiles.length * 100;

    const loadedPercent: number =  _.reduce(this.selectedFiles, (sum: number, file: any) => {
      return sum + file.progress.data.percentage;
    }, 0);
    return Math.floor(loadedPercent * 100 / totalPercent);
  }

  get uploadProgressSeconds(): number {
    if (!this.selectedFiles.length) { return 0; }

    return _.reduce(this.selectedFiles, (result: number, file: any) => {
      return result + _.get(file, 'progress.data.eta', 0);
    }, 0) / this.selectedFiles.length;
  }

  get canDropFiles(): boolean {
    return !(this.duplicatedAssets.length || this.invalidAssets.length || this.assets.length)
      || (this.isEditable && !this.duplicatedAssets.length && !this.invalidAssets.length);
  }

  /*******************************************
    * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.sortModel = new SortModel();
  }

  ngOnDestroy() {
    this.clearData();
    this.isActive = false;
    this.ref.detach();
  }

  onModalHideHook() {
    this._renderer.removeClass(document.body, 'page-modal-open');
  }

  /*******************************************
    * COMPONENT HANDLERS
  *******************************************/

  onSelectAllChange(selectedAllChecked: boolean) {
    this.selectedAllChecked = selectedAllChecked;
    if (selectedAllChecked) {
      this.pushAllSelectedItems();
    } else {
      this.pullAllSelectedItems();
    }
  }

  onSortChanged(event: any) {
    this.sortModel.column = event.column;
    this.sortModel.sort = event.sort;
    this.assets = _.orderBy(this.assets, [this.sortModel.column], [this.sortModel.sort]);
  }

  onUploadOutput(output: UploadOutput) {
    if (output.type === 'addedToQueue') {
      if (!this.canDropFiles) { return; }
      this.files.push(output.file);
    } else if (output.type === 'allAddedToQueue') {
      if (!this.canDropFiles) { return; }
      this.createAssets(this.files);
      if (_.size(this.assets)) {
        // check if assets have duplicates
        this.updateAssetValidation(this.assets)
        .pipe(
            first(),
            takeWhile(() => this.isActive)
        )
        .subscribe(() => {
          // instantly run uploading all valid assets
          if (_.size(this.assets)) {
            _.delay(() => {
              if (!this.isUploadStartedAndNotFinished) {
                this.startUploadProcess(this.assets);
              }
            }, 500);
          // or show invalid assets or duplicates
          } else if (_.size(this.invalidAssets)) {
            this.isInstantUploadingDone = true;
            this.displayInvalidAssets();
          } else if (_.size(this.duplicatedAssets)) {
            this.isInstantUploadingDone = true;
            this.displayDuplicates();
          }
        });
      } else if (_.size(this.invalidAssets)) {
        this.isInstantUploadingDone = true;
        this.displayInvalidAssets();
      }
    } else if (output.type === 'done') {
      this.handleUploadingDone(output);
    } else if (output.type === 'dragOver') {
      this.dragOver = true;
    } else if (output.type === 'dragOut') {
      this.dragOver = false;
    } else if (output.type === 'drop') {
      this.dragOver = false;
    }
  }

  onOverwriteClick() {
    this.startUploadProcess(this.selectedAssets);
  }

  onAddDuplicateClick() {
    _.each(this.selectedAssets, (asset: DigitalAsset) => {
      asset.duplicate = true;
    });
    this.startUploadProcess(this.selectedAssets);
  }

  onOkInvalidClick() {
    this.invalidAssets = [];
    if (_.size(this.duplicatedAssets)) {
      this.displayDuplicates();
    } else {
      this.clearData();
    }
    this.ref.markForCheck();
  }

  onCancelUploading() {
    this.uploadInput.emit({ type: 'cancelAll' });
    _.defer(() => {
      this.hide();
      this.uploadModalClosed.emit();
    });
  }

  onModalClose() {
    if (this.isUploadStartedAndNotFinished) {
      if (this.warningModal instanceof SimpleConfirmModalComponent) {
        this.warningModal.show();
      }
      _.defer(() => this.ref.markForCheck());
    } else {
      this.hide();
      this.uploadModalClosed.emit();
    }
  }

  onSaveClick() {
    if (!this.selectedAssets.length) { return; }
    this.assetsSaved.emit(this.selectedAssets);
    this.successModalShow.emit();
  }

  onChangeFormControl(event) {
    const { row, column, value } = event;
    this.assets = this.assets.map(asset => {
      if (asset.filename === row.filename) {
        asset[column.col] = value.option;
        return asset;
      }
      return asset;
    });
  }

  /*******************************************
    * COMPONENT PUBLIC METHODS
  *******************************************/

  public show() {
    this.columns = columns;
    this.isInstantUploadingDone = false;
    this._renderer.addClass(document.body, 'page-modal-open');
    if (this.defaultModal instanceof ModalDirective) {
      this.defaultModal.show();
    }
    this.isModalShown = true;
    this.ref.markForCheck();
    if (this.isEditable) {
      this.displayEditable();
    }
  }

  public hide() {
    this.clearData();
    if (this.defaultModal instanceof ModalDirective) {
      this.defaultModal.hide();
    }
    this.isModalShown = false;
    this.ref.markForCheck();
  }

  public startUploadProcess(assets: Array<DigitalAsset>) {
    this.isUploadInProgress = true;
    const delta = 1000;
    const chunk = 100;
    let i = 0;
    const interval = 2000;
    let timer = 0;

    _.forEach(assets, (asset: DigitalAsset) => {
      // add to selected files for watching statuses
      const fileIndex = this.getInputFileIndex(asset.filename);
      this.selectedFiles.push(this.files[fileIndex]);
    });

    // add to selected files for watching statuses

    _.forEach(_.chunk(assets, chunk), (selectedAssets) => {

      timer = interval * i++;

      _.delay((_selectedAssets: Array<DigitalAsset>) => {
        _.forEach(_selectedAssets, (asset: DigitalAsset) => this.uploadAsset(asset));
        }, timer, selectedAssets);
      });
  }


  /*******************************************
    * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Create assets from uploaded files
    * Instantly separate assets with invalid names
  */
  private createAssets(files: Array<any>) {
    _.forEach(files, (uploadedFile: any) => {
      const file: any = uploadedFile.nativeFile;
      if (!isValidAssetType(file.name)) {
        // this.invalidFileTypeSelected.emit(file);
        return;
      }

      const asset: any = createDigitalAsset(file);

      // separate valid and invalid assets
      if (!asset.isValidName) {
        asset.uncheckable = false;
        this.invalidAssets.push(asset);
        return;
      }

      asset.selected = true;
      asset.valid = true;

      const assetCategory = getAssetCategory(asset);
      if (assetCategory !== 'image') {
        asset.thumbnail = getUploaderAssetThumbnail(asset);
      }

      this.assets.push(asset);
    });
    this.assets = _.uniqBy(this.assets, 'filename');
    this.invalidAssets = _.uniqBy(this.invalidAssets, 'filename');
  }

  /*
    * Verify if assets has duplicates
    * Move all duplicate assets from assets to duplicatedAssets array
  */
  private updateAssetValidation(assets: Array<DigitalAssetRow>) {
    let i = 0;
    const interval = 2000;
    let timer = 0;
    // array for validation requests
    const subject = new Subject();
    const requestArray: any[] = [];
    // add delay to show loader for long system processes only
    _.delay(() => {
      if (!subject.isStopped) {
        this.startLoading();
      }
    }, 2000);

    _.forEach(_.chunk(assets, 20), (assetRows: Array<DigitalAssetRow>) => {
      const filenames = _.map(assetRows, 'filename');
      requestArray.push(this.damService.getFilenamesAvaliability(this.brandId, filenames).pipe(delay(timer)));

      /* throttle the calls so we do not reach the servers throttle limit */
      timer = interval * i++;
    });

    forkJoin(requestArray)
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any[]) => {
      _.forEach(data, (item: any) => {
        _.forEach(item, (result: any) => {
          const asset = _.find(assets, ['filename', result.filename]);
          if (asset) {
            asset.isDuplicate = !result.available;
            if (asset.isDuplicate) {
              if (!_.find(this.duplicatedAssets, {filename: asset.filename})) {
                _.remove(this.assets, asset);
                this.duplicatedAssets.push(asset);
              }
            }
          }
        });
      });
      subject.next();
      subject.complete();
      this.stopLoading();
    }, (error: any) => {
      subject.next();
      subject.complete();
      this.stopLoading();
    });
    return subject;
  }

  private pushAllSelectedItems() {
    _.forEach(this.assets, (item: any) => {
      if (!item.uncheckable) {
        item.selected = true;
      }
    });
  }

  private pullAllSelectedItems() {
    _.forEach(this.assets, (item: any) => {
      if (!item.uncheckable) {
        item.selected = false;
      }
    });
  }

  private uploadAsset(asset: DigitalAsset) {
    const fileIndex = this.getInputFileIndex(asset.filename);
    asset.uploadStatus = UploadStatus.Uploading;
    this.ref.detectChanges();

    const event: UploadInput = {
      type: 'uploadFile',
      url: `${Config.API}/assets-upload/${this.brandId}`,
      method: 'POST',
      file: this.files[fileIndex],
      data: {},
      headers: {
        'Authorization': `Bearer ${this.authService.token()}`,
      },
      withCredentials: false,
    };
    if (asset.duplicate) {
      event.data.makeCopyIfDuplicate = '1';
    }
    this.uploadInput.emit(event);
  }

  private getInputFileIndex(filename: string): number {
    // @ts-ignore
      return _.findIndex(this.files, {name: filename});
  }

  private clearData() {
    this.assets = [];
    this.files = [];
    this.isUploadInProgress = false;
    this.isInstantUploadingDone = false;
    this.selectedFiles = [];
    if (this.dragAssetsSpace) { this.dragAssetsSpace.clearData(); }

    this.duplicatedAssets = [];
    this.invalidAssets = [];
    this.displayingDuplicates = false;
    this.selectedAllChecked = false;
  }

  private startLoading() {
    if (this.loader instanceof LoaderComponent) {
      this.loader.show();
    }
    this.ref.markForCheck();
  }

  private stopLoading() {
    _.delay(() => {
      if (this.loader instanceof LoaderComponent) {
        this.loader.hide();
      }
      this.ref.markForCheck();
    }, 1000);
  }

  private displayDuplicates() {
    this.displayingDuplicates = true;
    this.assets = this.duplicatedAssets;
    this.columns = duplicateAssetsColumns;
    this.ref.markForCheck();
  }

  private displayInvalidAssets() {
    this.assets = this.invalidAssets;
    this.columns = columns;
    this.ref.markForCheck();
  }

  private displayEditable() {
    this.columns = editableAssetsColumns;
    this.ref.markForCheck();
  }

  // todo: rewrite
  private handleUploadingDone(output: UploadOutput): void {
    const asset = _.find(this.assets, ['filename', output.file.nativeFile.name]);
    asset.uploadStatus = UploadStatus.Done;

    const response = _.get(output, 'file.response');
    // save asset into uploadedAssets
    this.uploadedAssets.push(response);

    this.ref.detectChanges();
    const allFilesUpladed: boolean = _.every(this.selectedFiles, (file: UploadFile) => {
      return _.get(file, 'progress.status') === UploadStatus.Done;
    });
    if (allFilesUpladed) {
      // emit uploadedAssets together, clear array
      this.assetsUploaded.emit(this.uploadedAssets);
      if (this.isEditable) { this.processedUploadedAssets = this.uploadedAssets; }
      this.uploadedAssets = [];

      if (!this.isInstantUploadingDone) { this.isInstantUploadingDone = true; }
      // if we have assets with invalid names after uploading, display them
      if (_.size(this.invalidAssets)) {
        this.displayInvalidAssets();
      } else if (_.size(this.duplicatedAssets)) {
        if (this.displayingDuplicates) {
          if (this.isEditable) {
            this.processedAssets = [...this.processedAssets, ...this.processedUploadedAssets];
            _.pullAll(this.duplicatedAssets, this.selectedAssets);
            if (!this.duplicatedAssets.length) {
              this.displayingDuplicates = false;
              this.assets = this.processedAssets.map(item => ({ ...item, selected: true }));
              this.displayEditable();
            }
          } else {
            // if duplicates are being displayed currently
            // remove uploaded assets from duplicates after uploading
            _.pullAll(this.assets, this.selectedAssets);
            if (!this.assets.length) {
              // show success modal if uploading went without errors
              this.successModalShow.emit();
            }
          }
        } else {
          this.displayDuplicates();
        }
      } else {
        if (!this.isEditable) {
          // show success modal if uploading went without errors
          this.successModalShow.emit();
        } else {
          this.assets = this.assets.map((item) => {
            return this.processedUploadedAssets.find(res => res.filename === item.filename);
          }).map(item => ({ ...item, selected: true }));
        }
      }
      this.isUploadInProgress = false;
      this.ref.markForCheck();
    }
  }
}
